import { toInt, toBool } from "./utils.js";
import { cart } from "./cart-controller.js";
import { UI_EVENTS, dispatchCartEvent } from "./cart-events.js";

const SELECTORS = {
  btnAvailable: '[data-add-to-cart-button-state="available"]',
  btnUnavailable: '[data-add-to-cart-button-state="unavailable"]',
  btnAlreadyPurchased: '[data-add-to-cart-button-state="already-purchased"]',
  btnAlreadyInCart: '[data-add-to-cart-button-state="already-in-cart"]',
};

class AddToCartButton extends HTMLElement {
  #isProcessing = false;
  #currentQty = 1;
  #qtyWrapper = null;
  #qtyDisplay = null;
  #qtyMinus = null;
  #qtyPlus = null;

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[AddToCartButton:init]", error);
    }
  }

  init() {
    this.#getDataAttributes();
    this.#validateDataAttributes();
    this.#bindElements();
    this.#bindQtyElements();
    this.setState();
    this.#bindEvents();
    this.#bindQtyEvents();
    // A variant selector on the page is the source of truth for the current
    // variant. Its initial variant:change can fire before this element upgrades
    // and starts listening, leaving variantId on the Liquid-rendered default
    // (paper). Now that our listener is bound, actively request the current
    // variant so we reconcile regardless of element upgrade order. No selector
    // on the page → no reply → our own default stands.
    document.dispatchEvent(new CustomEvent("variant:request"));
    // Reconcile the button state with the live cart (covers reloads where the
    // eBook variant is loaded while the paper bundle is already in the cart).
    void this.#syncCartState();
  }

  #getDataAttributes() {
    const ds = this.dataset;
    this.variantId = toInt(ds.addToCartButtonVariantId);
    this.productId = toInt(ds.addToCartButtonProductId);
    const siblingProductId = toInt(ds.addToCartButtonSiblingProductId);
    this.siblingProductId =
      Number.isInteger(siblingProductId) && siblingProductId > 0
        ? siblingProductId
        : null;
    this.quantity = toInt(ds.addToCartButtonQuantity, 1);
    this.isAvailable = toBool(ds.addToCartButtonAvailable);
    this.alreadyInCart = toBool(ds.addToCartButtonInCart);
    this.alreadyPurchased = toBool(ds.addToCartButtonPurchased);
    this.isDigital = toBool(ds.addToCartButtonDigital);
    this.isInEditor = toBool(ds.addToCartButtonInEditor);
  }

  #validateDataAttributes() {
    if (!Number.isInteger(this.variantId) || this.variantId <= 0) {
      throw new Error(`[AddToCartButton] Invalid variant ID: ${this.variantId}`);
    }

    if (!Number.isInteger(this.quantity) || this.quantity <= 0) {
      this.quantity = 1;
    }
  }

  #bindElements() {
    this.buttons = {
      available: this.querySelector(SELECTORS.btnAvailable) || null,
      unavailable: this.querySelector(SELECTORS.btnUnavailable) || null,
      alreadyPurchased:
        this.querySelector(SELECTORS.btnAlreadyPurchased) || null,
      alreadyInCart: this.querySelector(SELECTORS.btnAlreadyInCart) || null,
    };
  }

  #bindQtyElements() {
    this.#qtyWrapper = this.querySelector("[data-qty-wrapper]");
    if (!this.#qtyWrapper) return;
    this.#qtyDisplay = this.#qtyWrapper.querySelector("[data-qty-display]");
    this.#qtyMinus = this.#qtyWrapper.querySelector("[data-qty-minus]");
    this.#qtyPlus = this.#qtyWrapper.querySelector("[data-qty-plus]");
  }

  #bindQtyEvents() {
    this.#qtyMinus?.addEventListener("click", () =>
      this.#updateQty(this.#currentQty - 1),
    );
    this.#qtyPlus?.addEventListener("click", () =>
      this.#updateQty(this.#currentQty + 1),
    );
  }

  #updateQty(val) {
    this.#currentQty = Math.max(1, val);
    if (this.#qtyDisplay) this.#qtyDisplay.textContent = this.#currentQty;
  }

  #updateQtyVisibility() {
    if (!this.#qtyWrapper) return;
    this.#qtyWrapper.classList.toggle("hidden", this.isDigital);
  }

  setState() {
    const buttonsArray = Object.values(this.buttons).filter(Boolean);

    if (this.isInEditor) {
      this.#showAllButtons(buttonsArray);
      return;
    }

    this.#hideAllButtons(buttonsArray);

    const active = this.#getActiveButton();
    active?.removeAttribute("hidden");
    this.#updateQtyVisibility();
  }

  #showAllButtons(buttonsArray) {
    buttonsArray.forEach((button) => button.removeAttribute("hidden"));
  }

  #hideAllButtons(buttonsArray) {
    buttonsArray.forEach((button) => button.setAttribute("hidden", ""));
  }

  #getActiveButton() {
    if (!this.isAvailable) return this.buttons.unavailable;
    if (this.isDigital && this.alreadyPurchased)
      return this.buttons.alreadyPurchased;
    if (this.isDigital && this.alreadyInCart) return this.buttons.alreadyInCart;
    return this.buttons.available;
  }

  #bindEvents() {
    if (this.buttons?.available) {
      this.buttons.available.addEventListener("click", this.#onClick.bind(this));
    }
    document.addEventListener("variant:change", this.#onVariantChange.bind(this));
  }

  #onVariantChange(e) {
    const { variantId, isDigital } = e.detail || {};
    if (variantId) {
      this.variantId = variantId;
    }
    if (typeof isDigital === "boolean") {
      this.isDigital = isDigital;
      if (isDigital) this.#updateQty(1);
    }
    this.setState();
    void this.#syncCartState();
  }

  #onClick(event) {
    event.preventDefault();
    if (this.#isProcessing) return;
    void this.addProductToCart().catch((error) =>
      console.error("[AddToCartButton:addProductToCart]", error),
    );
  }

  async addProductToCart() {
    this.#isProcessing = true;
    this.#updateProcessingUI();
    try {
      // Bundle rule: the physical (paper) variant already includes the eBook.
      if (this.isDigital) {
        // Adding the eBook while the paper bundle of the same product is already
        // in the cart would duplicate the eBook — skip the add and just show the
        // cart (the product is already covered by the bundle).
        if (await this.#physicalBundleInCart()) {
          this.alreadyInCart = true;
          this.setState();
          dispatchCartEvent(UI_EVENTS.CART_OPEN_REQUESTED, {
            source: "add-to-cart-button",
          });
          return;
        }
      } else {
        // Adding the paper bundle: remove a redundant standalone eBook of the
        // same product first, so it is not paid for twice. Done before the add
        // so the drawer opens on the final state; cart.add() reloads right after.
        await this.#removeRedundantDigitalSibling();
      }
      const properties = this.#collectProperties();
      await cart.add({
        id: this.variantId,
        quantity: this.#currentQty,
        properties,
      });
      this.alreadyInCart = true;
      this.setState();
    } finally {
      this.#isProcessing = false;
      this.#updateProcessingUI();
    }
  }

  /**
   * True if the cart item's product carries the same pattern content as this
   * one: the product itself, or — for split combined products (SEWR-T170) —
   * its paper/eBook partner referenced via custom.sibling_product.
   */
  #isSameBundle(itemProductId) {
    if (itemProductId === this.productId) return true;
    return (
      this.siblingProductId !== null && itemProductId === this.siblingProductId
    );
  }

  /**
   * Remove any standalone digital variant (requires_shipping === false) of this
   * product or its split sibling currently in the cart. Called before adding
   * the physical bundle variant. No-op if none present. Silent (no event) — the
   * following cart.add() reloads the cart and dispatches UPDATED.
   */
  async #removeRedundantDigitalSibling() {
    if (!this.productId) return;
    try {
      const state = await (
        await fetch(`${window.ThemeRoutes.cart}.js`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
      ).json();

      const redundant = (state.items || []).filter(
        (item) =>
          this.#isSameBundle(item.product_id) &&
          item.requires_shipping === false,
      );
      if (!redundant.length) return;

      const updates = {};
      redundant.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch(`${window.ThemeRoutes.cartUpdate}.js`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        throw new Error(`Cart cleanup failed (${response.status})`);
      }
    } catch (error) {
      // Don't block the add if cleanup fails; log and continue.
      console.error("[AddToCartButton:removeRedundantDigitalSibling]", error);
    }
  }

  /**
   * True if a physical (requires_shipping) variant of this product or its
   * split sibling — the paper bundle that includes the eBook — is already in
   * the cart. Used to prevent adding a redundant standalone eBook. Reads live
   * cart state so it holds regardless of which variant the page was loaded with.
   */
  async #physicalBundleInCart() {
    if (!this.productId) return false;
    try {
      const state = await (
        await fetch(`${window.ThemeRoutes.cart}.js`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
      ).json();
      return (state.items || []).some(
        (item) =>
          this.#isSameBundle(item.product_id) &&
          item.requires_shipping === true,
      );
    } catch (error) {
      // On failure, don't block the add.
      console.error("[AddToCartButton:physicalBundleInCart]", error);
      return false;
    }
  }

  /**
   * Reconcile alreadyInCart with the live cart and refresh the button. Needed
   * because the Liquid-rendered state reflects only the initially loaded variant
   * and is not recomputed on variant switch or reload. For the eBook variant,
   * the paper bundle of the same product also counts as "in cart" (it includes
   * the eBook), so the button shows "already in cart" instead of an add button
   * that silently does nothing.
   */
  async #syncCartState() {
    try {
      const state = await (
        await fetch(`${window.ThemeRoutes.cart}.js`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
      ).json();
      const items = state.items || [];

      let inCart = items.some((item) => item.id === this.variantId);
      if (!inCart && this.isDigital) {
        inCart = items.some(
          (item) =>
            this.#isSameBundle(item.product_id) &&
            item.requires_shipping === true,
        );
      }

      if (inCart !== this.alreadyInCart) {
        this.alreadyInCart = inCart;
        this.setState();
      }
    } catch (error) {
      console.error("[AddToCartButton:syncCartState]", error);
    }
  }

  #collectProperties() {
    const scope = this.closest("section") || document.body;
    const inputs = scope.querySelectorAll(
      'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]',
    );
    const properties = {};
    for (const input of inputs) {
      if (input.disabled) continue;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      const key = match[1];
      const value =
        input.type === "checkbox" || input.type === "radio"
          ? input.checked
            ? input.value
            : ""
          : input.value;
      if (value === "" || value == null) continue;
      properties[key] = value;
    }
    return properties;
  }

  #updateProcessingUI() {
    const availableButton = this.buttons?.available;
    if (availableButton) {
      availableButton.setAttribute(
        "aria-busy",
        this.#isProcessing ? "true" : "false",
      );
      availableButton.setAttribute(
        "aria-disabled",
        this.#isProcessing ? "true" : "false",
      );
      if ("disabled" in availableButton) {
        availableButton.disabled = this.#isProcessing;
      }
    }
  }
}

if (!customElements.get("add-to-cart-button")) {
  customElements.define("add-to-cart-button", AddToCartButton);
}